import { Locale } from 'antd/es/locale-provider';
import localeUs from 'antd/es/locale/en_US';
import localeBr from 'antd/es/locale/pt_BR';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import { ItemTemporality } from "../../../model/ItemModel";
import { GetAllItemTemporality, GetAllTemporalityByFilterDate, GetEnumTemporality } from "../../../services/Item/ItemServicesGet";
import GridArchive from './GridArchive/GridArchive';
import { KeyDrivePageContainer } from '../../../components/KeydriveComponents/PageContainer/KeyDrivePageContainer';
import { Check, CheckCircle, FilterAlt, WatchLater } from '@mui/icons-material';
import { Box, Button, CircularProgress, Grid, Grow, IconButton, MenuItem, Paper, Popper, Select, SelectChangeEvent, Typography } from '@mui/material';
import { KeyDrivePrimaryButton } from '../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton';
import { KeyDriveBodyMin } from '../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin';
import KeydriveSnackbar from '../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar';
import { KeyDriveSearchBar } from '../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBar';
import { KeyDriveSecondaryButton } from '../../../components/KeydriveComponents/Buttons/KeyDriveSecondaryButton';
import KeyDriveDatePicker from '../../../components/KeydriveComponents/Textfield/KeydriveDatePicker';
import styles from '../../Feed/Feed.module.css';
import { KeyDriveBodyMax } from '../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMax';
import moment from 'moment';

function useVisibility(
  cb: (isVisible: boolean) => void,
  deps: React.DependencyList
): (node: any) => void {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  return useCallback((node) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      cb(entry.isIntersecting);
    });

    if (node) intersectionObserver.current.observe(node);
  }, deps);
}

const TemporalityList: React.FC<any> = () => {

  let endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);

  const [limit] = useState<number>(15);
  let [skip, setSkip] = useState<number>(0);
  const [temporalityItens, setTemporalityItens] = useState<ItemTemporality[]>([]);
  let [temporalityMax, setTemporalityMax] = useState<boolean>(true);
  const [selectDateBegin, setSelectDateBegin] = useState<Date | undefined>(new Date());
  const [selectDateEnd, setSelectDateEnd] = useState<Date | undefined>(endDate);
  const [localization, setLocalization] = useState<Locale>();
  const [reload, setReload] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState<any>([]);
  const [keySelect, setKeySelect] = useState<string | number | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [severity, setSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('info');
  const { idWorkspace } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    getAllTemporalityItems();
  }, []);

  useEffect(() => {
    getAllTemporalityItemsSearch();
  }, [searchTerm]);

  function handleToggle() {
      setOpen((prevOpen) => !prevOpen);
  };

  const handleClickStopPropagation = (event: React.MouseEvent) => {
      event.stopPropagation();
  };

  const reset = () => {
      let endDate = new Date();
      endDate.setDate(endDate.getDate() + 1); 

      setSelectDateEnd(endDate);
      setSelectDateBegin(new Date());
      setKeySelect(undefined);
      getAllTemporalityItems();
      handleToggle();
  }

  async function getAllTemporalityItems() {
    setReload(true);
    setSkip(0);
    setTemporalityMax(true);
    try {
      const response = await GetAllItemTemporality(idWorkspace!, 0, limit, "");

      if (response == undefined || response.length <= 0) {
        setTemporalityItens([]);
      }

      setTemporalityItens(response);
    } catch (err: any) {

      setSeverity('error');
      setMessage(err.response.data || t("Temporality.errorLoad"));

    } finally { 

      setReload(false);

    }
  }

  async function getAllTemporalityItemsSearch() {
    setReload(true);
    setSkip(0);
    setTemporalityMax(true);
    try {
      const response = await GetAllItemTemporality(idWorkspace!, 0, 999, searchTerm);

      if (response == undefined || response.length <= 0) {
        setTemporalityItens([]);
      }

      setTemporalityItens(response);
    } catch (err: any) {

      setSeverity('error');
      setMessage(err.response.data || t("Temporality.errorLoad"));

    } finally { 

      setReload(false);

    }
  }

  async function getTemporalityItemsByDate() {
      setReload(true);
      setSkip(0);
      setTemporalityMax(true);
      GetAllTemporalityByFilterDate(idWorkspace!, Number(keySelect), selectDateBegin?.toDateString()!, 
        selectDateEnd?.toDateString()!, 0, limit)
      .then((temporality) => {
        if (temporality.length < limit) {
          setTemporalityMax(false);
        }

        setTemporalityItens(temporality);
        handleToggle();
      })
      .finally(() => {
        setReload(false)
      });
  }


  useEffect(() => {
    let optionInter: any = [];
    GetEnumTemporality()
      .then((enumReturn) => {
        enumReturn.map((data) => {
          optionInter.push({
            key: data.index,
            text: data.name
          });
        })
      })

    setOptions(optionInter)
  }, [localization]);

  useEffect(() => {
    if (i18n.language === 'pt-BR') {
      setLocalization(localeBr);
    } else {
      setLocalization(localeUs);
    }
  }, [i18n.language]);

  const lastBook = useVisibility(
    (visible) => {
      if (!visible) return;
      if (!idWorkspace) return;

      if (selectDateBegin?.toDateString() && selectDateEnd?.toDateString()) {
        GetAllTemporalityByFilterDate(
          idWorkspace,
          Number(keySelect),
          selectDateBegin.toDateString(),
          selectDateEnd.toDateString(),
          skip,
          limit
        ).then((temporality) => {
          if (temporality.length < limit) {
            setTemporalityMax(false);
          }
          setSkip(prev => prev + temporality.length);
          setTemporalityItens(prev => [...prev, ...temporality]);
        });
      } else {
        GetAllItemTemporality(idWorkspace, skip, limit, "").then((temporality) => {
          if (temporality.length < limit) {
            setTemporalityMax(false);
          }
          setSkip(prev => prev + temporality.length);
          setTemporalityItens(prev => [...prev, ...temporality]);
        });
      }
    },
    [idWorkspace, skip]
  );

  return (
    <KeyDrivePageContainer
      icon={<WatchLater fontSize='small' sx={{ color: "#22638D" }} />}
      title={t("Temporality.message")}
      subtitle={t("Temporality.subtitle")}
      divider
    >
      <Grid container display="flex" alignItems="center" spacing={1} sx={{ marginBottom: "10px" }}>
        <Grid item xs={11}>
          <KeyDriveSearchBar 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid item xs={1} display="flex" justifyContent="center" alignItems="flex-end">
          <Button
              sx={{
                  border: '1px solid #22638D',
                  borderRadius: '6px'
              }}
              ref={anchorRef}
              id="composition-button"
              aria-controls={open ? 'composition-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
          >
              <FilterAlt sx={{ color: '#22638D' }} />
              <KeyDriveBodyMin 
                color=''
                needBold={false}
                needEllipses={false}
                text={t("Temporality.FilterByStatus")}
              />

              <Popper
                sx={{ margin: "8px 0 0 0 !important" }}
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                disablePortal
              >
                {({ TransitionProps }) => (
                  <Grow
                    {...TransitionProps}
                    style={{ transformOrigin: "right top" }}
                  >
                    <Paper
                      onClick={handleClickStopPropagation}
                      sx={{
                        width: 340,
                        border: "1px solid #22638D",
                        boxShadow: "3px 3px 6px #22638D33",
                        borderRadius: "6px",
                        p: 2,
                      }}
                    >
                        <Grid container spacing={2}>

                          {/* TITLE */}
                          <Grid item xs={12} display="flex" alignItems="flex-start" gap={1}>
                            <KeyDriveBodyMax
                              text={t("Feed.Filters")}
                              color="#2b2b2b"
                              needBold
                              needEllipses={false}
                            />
                          </Grid>

                          <Grid item xs={12} display="flex" alignItems="flex-start" gap={1}>
                            <KeyDriveBodyMin
                              text={t("Temporality.FilterByStatus")}
                              color="#2b2b2b"
                              needBold={false} 
                              needEllipses={false}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Select
                              fullWidth
                              variant="standard"
                              value={keySelect}
                              onChange={(e) => setKeySelect(e.target.value)}
                              displayEmpty
                            >
                              {options.map((opt: any) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                  <KeyDriveBodyMin needBold={false} needEllipses={false} text={opt.text} color="#2b2b2b" />
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>

                          <Grid item xs={12} display="flex" alignItems="flex-start" gap={1}>
                            <KeyDriveBodyMin
                              text={t("Temporality.FilterByDate")}
                              color="#2b2b2b"
                              needBold={false} 
                              needEllipses={false}
                            />
                          </Grid>

                          <Grid item xs={12} display="flex" flexDirection="column" gap={1}>
                            <KeyDriveDatePicker
                              disabled={keySelect == undefined}
                              minDate={new Date("1970-01-01")}
                              onChange={(date) => setSelectDateBegin(date || undefined)}
                              value={selectDateBegin!}
                              maxDate={new Date("2100-12-31")}
                            />
                            <KeyDriveDatePicker
                              disabled={keySelect == undefined}
                              minDate={new Date("1970-01-01")}
                              onChange={(date) => setSelectDateEnd(date || undefined)}
                              value={selectDateEnd!}
                              maxDate={new Date("2100-12-31")}
                            />
                          </Grid>

                          <Grid item xs={12} display="flex" justifyContent="space-around" alignItems="center" gap={1}>
                            <KeyDrivePrimaryButton 
                              onClick={() => getTemporalityItemsByDate()}
                              startIcon={<Check />}
                              TextComponent={KeyDriveBodyMin}
                              TextComponentProps={{
                                color: "white",
                                needBold: false,
                                needEllipses: false,
                                text: t("Temporality.Apply")
                              }}
                            />
                            <KeyDriveSecondaryButton 
                              onClick={() => reset()}
                              TextComponent={KeyDriveBodyMin}
                              TextComponentProps={{
                                color: "#2b2b2b",
                                needBold: false,
                                needEllipses: false,
                                text: t("Feed.Reset")
                              }}
                            />
                          </Grid>

                        </Grid>
                      </Paper>
                    </Grow>
                  )}
              </Popper>
          </Button>
        </Grid>
      </Grid>
      { reload ? (
        <CircularProgress />
      ) : (
        <GridArchive
          itemTemporalities={temporalityItens}
          ref={temporalityMax ? lastBook : null}
        />
      ) }

      <KeydriveSnackbar 
        open={message != undefined}
        message={message}
        onClose={() => setMessage(undefined)}
        variant={severity}
      />
    </KeyDrivePageContainer>
  );
}

interface IApplyFilterComponent {
  value: string;
  type: "Deadline" | "Type";
}

export const ApplyFilterComponent = ({
  type,
  value
}: IApplyFilterComponent) => {
  return (
    <Box
      sx={{
        width: "fit-content",
        height: 28,
        opacity: 1,
        borderRadius: "4px",
        paddingTop: "4px",
        paddingRight: "8px",
        paddingBottom: "4px",
        paddingLeft: "8px",
        gap: "4px",
        background: "#22638D",
        color: "#FFFFFF",
        display: "flex",
      }}
    >
      { type == "Deadline" ? (
        <KeyDriveBodyMin 
          color='#FFFFFF'
          text={`Vencimento: ${value}`}
          needBold={false}
          needEllipses={false}
        />
      ) : (
        <KeyDriveBodyMin 
          color='#FFFFFF'
          text={`Filtrar por status: ${value}`}
          needBold={false}
          needEllipses={false}
        />
      ) }
      <CheckCircle sx={{ color: "#FFFFFF" }} fontSize='small' />
    </Box>
  );
}

export default TemporalityList;